import React, { useState, useEffect } from "react";
import { Row, Col, Typography, Select, Button, message, Modal, Input } from "antd";
import { MailOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { actions, reducer, sliceKey } from "./slice";
import { reportExportSaga } from "./saga";
import {
  selectReportExportLoading,
  selectReportExportError,
  selectReportExportMessage,
} from "./selectors";
import { useAuth } from "../../../contexts/auth";
import ReportJobsModal from "../ReportJobsModal";

const { Text } = Typography;

const ReportConfig = ({ filters = {} }) => {
  const REPORT_PARAM_CONFIG = {
    orderDetails: ["companyIds", "projectCodeIds"],
    sampleDetails: ["companyIds", "projectCodeIds"],
    boxReport: ["companyIds", "projectCodeIds"],
    correctionReport: ["companyIds", "projectCodeIds"],
    complianceReport: ["companyIds", "projectCodeIds"],
    complianceMonitoringData: ["companyIds", "projectCodeIds"],
  };

  const [reportType, setReportType] = useState("orderDetails");
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [jobsModalVisible, setJobsModalVisible] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  
  const auth = useAuth();
  const user = auth.loggedInUser;
  const dispatch = useDispatch();
  
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: reportExportSaga });

  const loading = useSelector(selectReportExportLoading);
  const error = useSelector(selectReportExportError);
  const messageFromApi = useSelector(selectReportExportMessage);

  const isExportDisabled = () => {
    const requiredParams = REPORT_PARAM_CONFIG[reportType] || [];
    return requiredParams.some(
      (param) => !filters?.[param] || filters[param].length === 0
    );
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleGenerateReportClick = () => {
    setEmailAddress(user?.email || '');
    setEmailModalVisible(true);
  };

  const handleViewJobsClick = () => {
    setJobsModalVisible(true);
  };

  const handleConfirmEmailAndGenerate = () => {
    if (!validateEmail(emailAddress)) {
      message.error('Please enter a valid email address.');
      return;
    }

    dispatch(
      actions.exportReport({
        filters: {
          ...filters,
          email: emailAddress
        },
        reportType,
        onSuccess: (responseData, apiMessage) => {
          if (apiMessage) {
            message.info(apiMessage); 
          }
          message.success({
            content: `Report generation started! You will receive an email at ${emailAddress} when the report is ready.`,
            duration: 6,
          });
          setEmailModalVisible(false);
          setEmailAddress('');
        },
        onFailure: (errorMessage) => {
          message.error(`Report generation failed: ${errorMessage}`);
          setEmailModalVisible(false);
        },
      })
    );
  };

  const handleCancelEmail = () => {
    setEmailModalVisible(false);
    setEmailAddress('');
  };

  useEffect(() => {
    return () => {
      dispatch(actions.clearReportData());
    };
  }, [dispatch]);

  return (
    <>
      <Row justify="space-between" align="middle" gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <Text strong>Report Type</Text>
          <Select
            value={reportType}
            onChange={setReportType}
            style={{ width: "100%", marginTop: 4 }}
            options={[
              { label: "Order Details", value: "orderDetails" },
              { label: "Sample Details", value: "sampleDetails" },
              { label: "Box Report", value: "boxReport" },
              { label: "Correction Report", value: "correctionReport" },
              { label: "Order‑Wise Key Compliance Report", value: "complianceReport" },
              { label: "Compliance Monitoring Data", value: "complianceMonitoringData" }
            ]}
          />
        </Col>
        <Col>
          <Button
            icon={<UnorderedListOutlined />}
            onClick={handleViewJobsClick}
            style={{ marginRight: 8 }}
          >
            View Report Jobs
          </Button>
          <Button
            type="primary"
            icon={<MailOutlined />}
            loading={loading}
            onClick={handleGenerateReportClick}
            disabled={isExportDisabled()}
          >
            Generate Report
          </Button>
        </Col>
      </Row>

      {/* Email Modal */}
      <Modal
        title="Generate Report via Email"
        visible={emailModalVisible}
        onOk={handleConfirmEmailAndGenerate}
        onCancel={handleCancelEmail}
        confirmLoading={loading}
        okText="Generate Report"
        cancelText="Cancel"
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Report Type: </Text>
          <Text>{reportType}</Text>
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <Text strong>Email Address:</Text>
          <Input
            placeholder="Enter email address where report will be sent"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            prefix={<MailOutlined />}
            style={{ marginTop: 8 }}
            size="large"
          />
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
            The report will be generated and sent to this email address (typically takes 5-15 minutes depending on data size).
          </Text>
        </div>

        {isExportDisabled() && (
          <div style={{ 
            backgroundColor: '#fff2e8', 
            border: '1px solid #ffbb96', 
            borderRadius: 4, 
            padding: 12, 
            marginTop: 16 
          }}>
            <Text type="warning">
              Please ensure all required filters are selected before generating the report.
            </Text>
          </div>
        )}
      </Modal>

      {/* Report Jobs Modal */}
      <ReportJobsModal
        visible={jobsModalVisible}
        onClose={() => setJobsModalVisible(false)}
        filters={filters}
      />
    </>
  );
};

export default ReportConfig;